import React, { useCallback } from "react";
import type { FormInstance } from "antd";
import { Form, Input, Button } from "../../../../shared";
import FeatureFlagGuard from "../../../../FeatureFlagGuard/FeatureFlagGuard";
import { usePamFeature } from "../../../../../hooks/useFeatureFlag";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import ScheduleField from "./ScheduleField";
import AudienceStep from "./AudienceStep";
import AwardFrequencyField from "./AwardFrequencyField";
import { formFieldTooltip } from "./formFieldTooltip";
import {
  DEFAULT_CAMPAIGN_TYPE,
  DEFAULT_SCHEDULE_MODE,
  DISPLAY_BANNER_CAMPAIGN_TYPE,
  SERIES_CAMPAIGN_TYPE,
  campaignTypeLabel,
  wizardInitialValuesForCampaignType,
} from "./toWizardCampaignIn";
import {
  AWARD_FREQUENCY,
  DEFAULT_CUSTOM_FREQUENCY_AMOUNT,
  DEFAULT_CUSTOM_FREQUENCY_UNIT,
  isInformationalBannerOnly,
  shouldShowAwardFrequency,
} from "./awardFrequency";
import type {
  PriorityLevel,
  WizardCampaignType,
  WizardFormValues,
  WizardMode,
  WizardStepFormValue,
  WizardTriggerMode,
} from "./toWizardCampaignIn";
import type { DynamicBonusCatalog } from "../../types";
import styles from "../../stylesDynamicBonuses.module.css";

const priorityPills = (): Array<{ level: PriorityLevel; label: string }> => [
  { level: "low", label: AppStrings.PAM_DYNAMIC_BONUS_PRIORITY_LOW },
  { level: "medium", label: AppStrings.PAM_DYNAMIC_BONUS_PRIORITY_MEDIUM },
  { level: "high", label: AppStrings.PAM_DYNAMIC_BONUS_PRIORITY_HIGH },
];

/** Pill selector for campaign priority (Low / Medium / High). Integrates with antd Form via value/onChange. */
const PriorityField: React.FC<{
  value?: PriorityLevel;
  onChange?: (v: PriorityLevel) => void;
  disabled?: boolean;
}> = ({ value = "medium", onChange, disabled = false }) => (
  <div>
    <div
      role="radiogroup"
      aria-label={AppStrings.PAM_DYNAMIC_BONUS_PRIORITY_ARIA}
      className={styles.pillGroup}
    >
      {priorityPills().map(({ level, label }) => (
        <button
          key={level}
          type="button"
          role="radio"
          aria-checked={value === level}
          disabled={disabled}
          onClick={() => onChange?.(level)}
          className={[styles.pill, value === level ? styles.pillSelected : ""]
            .join(" ")
            .trim()}
        >
          {label}
        </button>
      ))}
    </div>
  </div>
);

type Props = {
  form: FormInstance<WizardFormValues>;
  /** Trigger mode to restore when leaving a series ("campaign_start" once
   * the catalog exposes it, "event" otherwise) — mirrors the wizard's
   * initial `trigger_mode`, computed once from the events catalog. */
  defaultTriggerMode: WizardTriggerMode;
  onNext?: () => void;
  /** "campaign" (default) shows schedule/priority; "template"
   * shows name + description only. */
  mode?: WizardMode;
  /** When false, omit the step footer (combined Details screen owns Next). */
  showFooter?: boolean;
  /** Prevents active campaign metadata and rule changes. */
  rulesLocked?: boolean;
  /** View-only host (series child, expired/archived). Dims Priority like source. */
  readOnly?: boolean;
  /**
   * When true (default for campaign mode hosts), Render Audience beside Run for.
   * Series hosts pass false — Braze series have no admin audience pick.
   */
  showAudience?: boolean;
  /**
   * Series-stamped child runs (and series edit) are Braze-delivered. Keep
   * both source pills visible like Priority, with Braze selected and both
   * disabled so the child is not editable as a PAM campaign.
   */
  lockBrazeSource?: boolean;
  catalog?: DynamicBonusCatalog;
};

/** Step 1 — campaign source, name, schedule, priority, and optional audience. */
const StartStep: React.FC<Props> = ({
  form,
  defaultTriggerMode,
  onNext,
  mode = "campaign",
  showFooter = true,
  rulesLocked = false,
  readOnly = false,
  showAudience = true,
  lockBrazeSource = false,
  catalog,
}) => {
  const campaignType =
    (Form.useWatch("campaign_type", form) as WizardCampaignType | undefined) ??
    DEFAULT_CAMPAIGN_TYPE;
  const isSeries = campaignType === SERIES_CAMPAIGN_TYPE;
  const brazeSourceSelected = isSeries || lockBrazeSource;
  const sourceDisabled = rulesLocked || lockBrazeSource;
  const isDisplayBanner = campaignType === DISPLAY_BANNER_CAMPAIGN_TYPE;
  const watchedSteps = Form.useWatch("steps", { form, preserve: true }) as
    | WizardStepFormValue[]
    | undefined;
  const { isEnabled: dynamicBonusesBetaMode } = usePamFeature(
    "dynamicBonusesBetaMode",
  );
  const showAwardFrequency = shouldShowAwardFrequency({
    isDisplayBanner:
      isDisplayBanner || isInformationalBannerOnly(watchedSteps),
    isBraze: brazeSourceSelected,
    betaMode: dynamicBonusesBetaMode,
  });
  const watchedDateRange = Form.useWatch("date_range", form) as
    | [string | null, string | null]
    | undefined;

  const clearTemplatePrefill = useCallback(() => {
    form.setFieldValue("created_from_template_id", null);
    form.setFieldValue("steps", [{}]);
    form.setFieldValue("max_awards_per_user", undefined);
    form.setFieldValue("award_frequency", AWARD_FREQUENCY.ONE_TIME);
    form.setFieldValue("award_frequency_amount", DEFAULT_CUSTOM_FREQUENCY_AMOUNT);
    form.setFieldValue("award_frequency_unit", DEFAULT_CUSTOM_FREQUENCY_UNIT);
    form.setFieldValue("award_frequency_exact_seconds", undefined);
    form.setFieldValue("extra_eligibility", undefined);
    form.setFieldValue("localized_strings", undefined);
    form.setFieldValue("audience_mode", "all");
    form.setFieldValue("segment_id", undefined);
    form.setFieldValue("trigger_mode", defaultTriggerMode);
    form.setFieldValue("trigger_event_key", undefined);
    form.setFieldValue("affiliate_id", undefined);
    form.setFieldValue("affiliate_referral_code", undefined);
    form.setFieldValue("bet_targeted_preset", null);
  }, [defaultTriggerMode, form]);

  /**
   * Switching campaign type resets the flow — series, display-banner, and
   * standard campaigns have incompatible trigger/schedule/step shapes.
   */
  const handleCampaignTypeSelect = useCallback(
    (type: WizardCampaignType) => {
      const previousType = form.getFieldValue("campaign_type") as
        | WizardCampaignType
        | undefined;
      if (
        previousType === type ||
        (type === DEFAULT_CAMPAIGN_TYPE &&
          previousType === DISPLAY_BANNER_CAMPAIGN_TYPE)
      ) {
        return;
      }
      clearTemplatePrefill();
      form.setFieldsValue(
        wizardInitialValuesForCampaignType(type) as never,
      );
      if (type === SERIES_CAMPAIGN_TYPE || type === DISPLAY_BANNER_CAMPAIGN_TYPE) {
        return;
      }
      form.setFieldValue("schedule_mode", DEFAULT_SCHEDULE_MODE);
      if (
        previousType === SERIES_CAMPAIGN_TYPE ||
        previousType === DISPLAY_BANNER_CAMPAIGN_TYPE
      ) {
        form.setFieldValue("trigger_mode", defaultTriggerMode);
        form.setFieldValue("steps", [{}]);
      }
    },
    [clearTemplatePrefill, defaultTriggerMode, form],
  );

  const isTemplateMode = mode === "template";

  return (
    <>
      <Form.Item name="campaign_type" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="start_mode" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="created_from_template_id" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="bet_targeted_preset" hidden>
        <Input />
      </Form.Item>

      {isTemplateMode ? (
        <Form.Item
          label={AppStrings.PAM_DYNAMIC_BONUS_NAME_LABEL}
          tooltip={formFieldTooltip(AppStrings.PAM_DYNAMIC_BONUS_NAME_TOOLTIP)}
          name="name"
          rules={[
            {
              required: true,
              message: AppStrings.PAM_DYNAMIC_BONUS_NAME_REQUIRED,
            },
          ]}
        >
          <Input placeholder={AppStrings.PAM_DYNAMIC_BONUS_TEMPLATE_NAME_PLACEHOLDER} />
        </Form.Item>
      ) : (
        <div className={styles.formFieldRow3}>
          <Form.Item
            label={AppStrings.PAM_DYNAMIC_BONUS_NAME_LABEL}
            tooltip={formFieldTooltip(AppStrings.PAM_DYNAMIC_BONUS_NAME_TOOLTIP)}
            name="name"
            rules={[
              {
                required: true,
                message: AppStrings.PAM_DYNAMIC_BONUS_NAME_REQUIRED,
              },
            ]}
          >
            <Input
              placeholder={
                isSeries
                  ? AppStrings.PAM_DYNAMIC_BONUS_SERIES_NAME_PLACEHOLDER
                  : isDisplayBanner
                    ? AppStrings.PAM_DYNAMIC_BONUS_BANNER_NAME_PLACEHOLDER
                    : AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_NAME_PLACEHOLDER
              }
            />
          </Form.Item>
          <Form.Item
            label={AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_TYPE_LABEL}
            tooltip={formFieldTooltip(AppStrings.PAM_DYNAMIC_BONUS_MESSAGE_TYPE_TOOLTIP)}
          >
            <div
              role="radiogroup"
              aria-label={AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_TYPE_LABEL}
              className={styles.pillGroup}
            >
              {lockBrazeSource ? (
                <>
                  <button
                    type="button"
                    role="radio"
                    aria-checked
                    disabled
                    className={[styles.pill, styles.pillSelected].join(" ")}
                  >
                    {campaignTypeLabel(SERIES_CAMPAIGN_TYPE)}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={false}
                    disabled
                    className={styles.pill}
                  >
                    {campaignTypeLabel(DEFAULT_CAMPAIGN_TYPE)}
                  </button>
                </>
              ) : (
                <>
                  <FeatureFlagGuard
                    feature="dynamicBonusesExternal"
                    mode="conditional"
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={brazeSourceSelected}
                      disabled={sourceDisabled}
                      onClick={() =>
                        handleCampaignTypeSelect(SERIES_CAMPAIGN_TYPE)
                      }
                      className={[
                        styles.pill,
                        brazeSourceSelected ? styles.pillSelected : "",
                      ]
                        .join(" ")
                        .trim()}
                    >
                      {campaignTypeLabel(SERIES_CAMPAIGN_TYPE)}
                    </button>
                  </FeatureFlagGuard>
                  <FeatureFlagGuard
                    feature="dynamicBonusesInternal"
                    mode="conditional"
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!brazeSourceSelected}
                      disabled={sourceDisabled}
                      onClick={() =>
                        handleCampaignTypeSelect(DEFAULT_CAMPAIGN_TYPE)
                      }
                      className={[
                        styles.pill,
                        !brazeSourceSelected ? styles.pillSelected : "",
                      ]
                        .join(" ")
                        .trim()}
                    >
                      {campaignTypeLabel(DEFAULT_CAMPAIGN_TYPE)}
                    </button>
                  </FeatureFlagGuard>
                </>
              )}
            </div>
          </Form.Item>
          <Form.Item
            label={AppStrings.PAM_DYNAMIC_BONUS_PRIORITY_LABEL}
            tooltip={formFieldTooltip(AppStrings.PAM_DYNAMIC_BONUS_PRIORITY_TOOLTIP)}
            name="priority"
          >
            <PriorityField disabled={rulesLocked || readOnly} />
          </Form.Item>
        </div>
      )}

      {isTemplateMode && (
        <Form.Item label={AppStrings.DESCRIPTION} name="description">
          <Input placeholder={AppStrings.PAM_DYNAMIC_BONUS_DESCRIPTION_PLACEHOLDER} />
        </Form.Item>
      )}

      {!isTemplateMode && !isSeries && (
        <>
          <div className={styles.detailsRunAudienceRow}>
            <Form.Item
              label={AppStrings.PAM_DYNAMIC_BONUS_RUN_FOR_LABEL}
              tooltip={formFieldTooltip(AppStrings.PAM_DYNAMIC_BONUS_RUN_FOR_TOOLTIP)}
              name="schedule_mode"
              className={styles.runForField}
            >
              <ScheduleField
                dateRangeValue={watchedDateRange}
                onDateRangeChange={(range) =>
                  form.setFieldValue("date_range", range)
                }
                disabled={rulesLocked}
              />
            </Form.Item>
            {showAwardFrequency && (
              <div className={styles.awardFrequencyField}>
                <AwardFrequencyField
                  form={form}
                  disabled={rulesLocked}
                  catalog={catalog}
                />
              </div>
            )}
            {showAudience ? (
              <div className={styles.audienceField}>
                <AudienceStep showFooter={false} rulesLocked={rulesLocked} />
              </div>
            ) : null}
          </div>
          {/* Hidden input, but the Form.Item stays visible so the custom-schedule
              validation error renders under the date pickers. */}
          <Form.Item
            name="date_range"
            dependencies={["schedule_mode"]}
            className={styles.hiddenInputFormItem}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value?: [string | null, string | null]) =>
                  getFieldValue("schedule_mode") !== "custom" ||
                  (value?.[0] && value?.[1])
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          AppStrings.PAM_DYNAMIC_BONUS_CUSTOM_SCHEDULE_REQUIRED,
                        ),
                      ),
              }),
            ]}
          >
            <Input type="hidden" />
          </Form.Item>
        </>
      )}

      {isTemplateMode && showAwardFrequency && (
        <AwardFrequencyField
          form={form}
          disabled={rulesLocked}
          catalog={catalog}
        />
      )}

      {showFooter && (
        <div className={styles.stepFooter}>
          <Button intent="primary" onClick={onNext}>
            {AppStrings.NEXT}
          </Button>
        </div>
      )}
    </>
  );
};

export default StartStep;
